import React, { useMemo } from 'react';
import { Autocomplete, AutocompleteItem, Chip } from '@heroui/react';
import { TDetailedPolygons, TFeature } from '../../../../main-map/mapTypes';

interface PortData {
	portName: string;
	polygonIds: string[];
	count: number;
}

interface PortMultiSelectorProps {
	detailedPolygons: TDetailedPolygons | null;
	selectedPortNames: string[];
	onSelectionChange: (portNames: string[], polygonIds: string[]) => void;
	isDisabled?: boolean;
	placeholder?: string;
	size?: 'sm' | 'md' | 'lg';
}

export const PortMultiSelector: React.FC<PortMultiSelectorProps> = ({
	detailedPolygons,
	selectedPortNames,
	onSelectionChange,
	isDisabled = false,
	placeholder = 'Select Ports',
	size = 'sm',
}) => {
	// Process polygon data to group by port name
	const portsData = useMemo<PortData[]>(() => {
		if (!detailedPolygons?.features) return [];

		// Group polygons by port name
		const portMap = new Map<string, string[]>();

		detailedPolygons.features
			.filter((feature: TFeature) => feature.properties)
			.forEach((feature: TFeature) => {
				const portName = feature.properties!.portName;
				const polygonId = feature.properties!.id;
				
				if (portName && polygonId) {
					if (!portMap.has(portName)) {
						portMap.set(portName, []);
					}
					portMap.get(portName)!.push(polygonId);
				}
			});

		// Convert to array and sort by port name
		const portsArray: PortData[] = Array.from(portMap.entries())
			.map(([portName, polygonIds]) => ({
				portName,
				polygonIds: [...new Set(polygonIds)], // Remove duplicates
				count: [...new Set(polygonIds)].length,
			}))
			.filter(port => port.portName.trim() !== '') // Filter out empty port names
			.sort((a, b) => a.portName.localeCompare(b.portName));

		return portsArray;
	}, [detailedPolygons]);



	const handleSelectionChange = (key: any) => {
		if (key === null || key === undefined) return;
		
		const selectedPortName = key as string;
		let updatedSelectedPorts: string[];

		// Toggle selection - if already selected, remove it; otherwise add it
		if (selectedPortNames.includes(selectedPortName)) {
			updatedSelectedPorts = selectedPortNames.filter(port => port !== selectedPortName);
		} else {
			updatedSelectedPorts = [...selectedPortNames, selectedPortName];
		}
		
		// Collect all polygon IDs for selected ports
		const allPolygonIds: string[] = [];
		updatedSelectedPorts.forEach(portName => {
			const portData = portsData.find(port => port.portName === portName);
			if (portData) {
				allPolygonIds.push(...portData.polygonIds);
			}
		});

		onSelectionChange(updatedSelectedPorts, allPolygonIds);
	};

	// Create display text for selected items
	const getDisplayText = () => {
		if (selectedPortNames.length === 0) return '';
		if (selectedPortNames.length === 1) {
			const portData = portsData.find(port => port.portName === selectedPortNames[0]);
			return `${selectedPortNames[0]} (${portData?.count || 0} polygons)`;
		}
		
		// Calculate total polygons for multiple selections
		const totalPolygons = selectedPortNames.reduce((sum, portName) => {
			const portData = portsData.find(port => port.portName === portName);
			return sum + (portData?.count || 0);
		}, 0);
		
		return `${selectedPortNames.length} ports selected (${totalPolygons} polygons)`;
	};

	return (
		<div className="w-full space-y-2">
			<Autocomplete
				placeholder={placeholder}
				size={size}
				isDisabled={isDisabled}
				className="max-w-full"
				onSelectionChange={handleSelectionChange}
				allowsCustomValue={false}
				aria-label="Select Ports"
			>
				{portsData.map((port) => (
					<AutocompleteItem key={port.portName} textValue={port.portName}>
						<div className="w-full min-w-0">
							<span className="truncate block" title={port.portName}>{port.portName}</span>
						</div>
					</AutocompleteItem>
				))}
			</Autocomplete>
			
			{/* Display selected ports as chips */}
			{selectedPortNames.length > 0 && (
				<div className="p-2 bg-blue-50 rounded-lg space-y-2">
					<div className="text-sm font-medium text-msi-midblue-color-500">
						{getDisplayText()}
					</div>
					<div className="flex flex-wrap gap-1">
						{selectedPortNames.map((portName) => {
							const portData = portsData.find(port => port.portName === portName);
							return (
								<Chip
									key={portName}
									size="sm"
									variant="flat"
									color="primary"
									onClose={() => {
										const updatedPorts = selectedPortNames.filter(p => p !== portName);
										const allPolygonIds: string[] = [];
										updatedPorts.forEach(pName => {
											const pData = portsData.find(port => port.portName === pName);
											if (pData) {
												allPolygonIds.push(...pData.polygonIds);
											}
										});
										onSelectionChange(updatedPorts, allPolygonIds);
									}}
								>
									{portName} ({portData?.count || 0})
								</Chip>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};